import React, { useState, useEffect, useRef } from 'react';
import { Mail, ChevronRight, Quote } from 'lucide-react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';

// Initialize EmailJS
emailjs.init("cfZ-p6Iwel_x5wFW9");

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Get reCAPTCHA token
    const token = recaptchaRef.current?.getValue();
    if (!token) {
      alert('Please complete the reCAPTCHA');
      return;
    }
    
    setSubmitStatus('sending');
    
    try {
      await emailjs.send(
        'service_n14glaq',
        'template_ks2mk5s',
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          to_email: 'warrencameron@gmail.com',
          'g-recaptcha-response': token,
        },
        'cfZ-p6Iwel_x5wFW9'
      );
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitStatus('error');
    } finally {
      // Reset reCAPTCHA
      recaptchaRef.current?.reset();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?auto=format&fit=crop&q=80"
            alt="Winsome Cameron"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-900/70"></div>
        </div>
        
        <div className="relative container mx-auto px-6 text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            {/* Removed "Winsome Cameron" */}
          </h1>
          <p className="text-2xl md:text-3xl text-gold-400 font-light mb-8">
            Empowering Others. Building Connections. Leading with Purpose.
          </p>
          <p className="max-w-2xl text-lg md:text-xl mb-12">
            A transformative leader and mentor dedicated to fostering growth, 
            championing diversity, and creating lasting positive impact in 
            both business and community.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-navy-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-start mb-12">
              <div className="order-2 md:order-1">
                <Quote className="h-12 w-12 text-gold-400 mb-8" />
                <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-8">
                  My Story
                </h2>
                <div className="space-y-6 text-navy-700">
                  <p className="text-lg">
                    Winsome Cameron was born and raised in England to Jamaican parents before emigrating to the United States in December 1980 at age 11. Though many years have passed, if you catch her at the right moment, you might still hear traces of her true British accent. She grew up in Queens, New York, where she graduated from August Martin High School, Class of '87—Go Falcons!
                  </p>
                  <p className="text-lg">
                    In 1987, Winsome began her college education at CUNY Queens College, where she pursued a dual major in Speech-Language Pathology and Sociology with a minor in Education. While attending college, she worked at South African Airways at JFK Airport, becoming the first African-American woman in the Northeast region to hold a front-end supervisory role. In that position, she was responsible for coordinating non-stop flights to Johannesburg, South Africa.
                  </p>
                  <p className="text-lg">
                    God's plan led her to a long and fulfilling career at the Long Island Rail Road, where she has worked as a Road Freight Conductor for almost 30 years. Throughout her tenure, Winsome has mentored countless coworkers, engaged in peer roundtable discussions, and earned a reputation as a knowledgeable and reliable team member. She looks forward to retiring in 2026.
                  </p>
                  <p className="text-lg">
                    As a member of the Zeta Amicae, Winsome embraces the motto, "Be a Friend, Be of Service." She thrives on empowering others and hopes to inspire the women around her to become leaders in their communities.
                  </p>
                  <p className="text-lg">
                    Winsome resides on Long Island with her fiancé, Thomas, and their three Shih Tzus—Goliath, Tattoo, and Minnie. She has a passion for travel, embracing new challenges, and building meaningful connections with others. While many have had the privilege of calling her friend, even more have experienced the true meaning of her friendship.
                  </p>
                </div>
              </div>
              
              <div className="order-1 md:order-2 sticky top-8">
                <img
                  src="/headshot/winsome_headshot.jpg"
                  alt="Winsome Cameron"
                  className="rounded-2xl shadow-xl w-full max-w-md mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-8">
              Let's Connect
            </h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <p className="text-lg text-navy-700 mb-8">
                  Whether you're interested in mentorship, speaking engagements, 
                  or collaboration opportunities, I'd love to hear from you.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-navy-700">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-1 block w-full rounded-md border-navy-300 shadow-sm 
                      focus:border-gold-400 focus:ring focus:ring-gold-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-navy-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-1 block w-full rounded-md border-navy-300 shadow-sm 
                      focus:border-gold-400 focus:ring focus:ring-gold-200 focus:ring-opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-navy-700">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="mt-1 block w-full rounded-md border-navy-300 shadow-sm 
                      focus:border-gold-400 focus:ring focus:ring-gold-200 focus:ring-opacity-50"
                  />
                </div>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="normal"
                  sitekey="6LdkmdAqAAAAAMQZAplssep211O7-P0elmFLISFp"
                  className="mb-4"
                />
                <button
                  type="submit"
                  disabled={submitStatus === 'sending'}
                  className="w-full bg-navy-900 text-white px-6 py-3 rounded-md 
                    font-semibold hover:bg-navy-800 transition-colors disabled:opacity-50"
                >
                  {submitStatus === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
                
                {submitStatus === 'success' && (
                  <p className="text-green-600 text-sm">
                    Thank you for your message! We'll get back to you soon.
                  </p>
                )}
                
                {submitStatus === 'error' && (
                  <p className="text-red-600 text-sm">
                    Sorry, there was an error sending your message. Please try again later.
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;